<template>
  <div class="h-full w-full flex justify-center items-center p-4" ref="editorContent">
    <canvas ref="canvasRef" style="max-width:100%;max-height:100%;" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { ElMessage } from 'element-plus'

const props = defineProps({
  imageWidth: { type: Number, default: 512 },
  imageHeight: { type: Number, default: 512 },
  backgroundColor: { type: String, default: '#FFFFFF' },
  zoomLevel: { type: Number, default: 1 },
  chartData: { type: Array, default: () => [] },
  orient: { type: String, default: 'horizontal' },
  nodeAlign: { type: String, default: 'right' },
  labelPosition: { type: String, default: 'top' },
  lineStyleColor: { type: String, default: 'source' },
  selectedLineColorCurveness: { type: Number, default: 0.1 },
  nodeWidth: { type: Number, default: 18 },
  nodeGap: { type: Number, default: 14 },
  lineOpacity: { type: Number, default: 0.42 },
  labelFontSize: { type: Number, default: 12 }
})

const canvasRef = ref(null)
const editorContent = ref(null)

const PAL = ['#5470c6', '#3ba272', '#ee6666', '#fac858', '#73c0de', '#9a60b4', '#fc8452', '#e07c2d', '#ea7ccc']

let S = { nodes: [], links: [], cfg: null, W: 512, H: 512 }
let drag = null
let resizeObserver = null
let rafId = null

// ── Helpers ──
const h2r = (c, a) => {
  if (!c) return `rgba(100,120,200,${a})`
  if (typeof c === 'string' && c.startsWith('rgba')) {
    const p = c.match(/[0-9.]+/g); if (p && p.length >= 3) return `rgba(${p[0]},${p[1]},${p[2]},${a})`
  }
  if (typeof c === 'string' && c.startsWith('#') && c.length >= 7)
    return `rgba(${parseInt(c.slice(1,3),16)},${parseInt(c.slice(3,5),16)},${parseInt(c.slice(5,7),16)},${a})`
  return c
}

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

const rrp = (ctx, x, y, w, h, r) => {
  r = Math.max(0, Math.min(r || 0, w / 2, h / 2))
  if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return }
  ctx.beginPath()
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r); ctx.closePath()
}

const drawLabel = (ctx, text, cx, cy, sty, meta = {}) => {
  if (sty.show === false) return
  const fw = sty.fontWeight || 'normal', fs = +(sty.fontSize || 12)
  ctx.font = `${fw} ${fs}px sans-serif`
  const tw = ctx.measureText(text).width
  const pad = Array.isArray(sty.padding) ? sty.padding : [+(sty.padding || 2), +(sty.padding || 4)]
  const [pv, ph] = pad
  const bw = tw + ph * 2, bh = fs + pv * 2
  const bx = cx - bw / 2, by = cy - bh / 2
  const bg = sty.backgroundColor, bc = sty.borderColor, bwd = +(sty.borderWidth || 0), br = +(sty.borderRadius || 0)
  if (bg && bg !== 'transparent' && bg !== 'none') { ctx.fillStyle = bg; rrp(ctx, bx, by, bw, bh, br); ctx.fill() }
  if (bwd > 0 && bc && bc !== 'transparent') {
    ctx.strokeStyle = bc; ctx.lineWidth = bwd
    const bt = sty.borderType || 'solid'
    ctx.setLineDash(bt === 'dashed' ? [5, 3] : bt === 'dotted' ? [2, 2] : [])
    rrp(ctx, bx, by, bw, bh, br); ctx.stroke(); ctx.setLineDash([])
  }
  let fillStyle = sty.color || '#333'
  const mode = sty.colorMode || 'solid'
  if (mode === 'source') fillStyle = meta.sourceColor || fillStyle
  else if (mode === 'target') fillStyle = meta.targetColor || fillStyle
  ctx.fillStyle = fillStyle; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(text, cx, cy)
}

// ── Layout ──
const calcLayers = (data, links, align) => {
  const m = {}
  data.forEach(d => { m[d.name] = { ...d, ins: [], outs: [], lL: -1, lR: -1 } })
  links.forEach(l => { m[l.source]?.outs.push(l); m[l.target]?.ins.push(l) })
  const list = Object.values(m)
  // forward pass
  list.filter(n => !n.ins.length).forEach(n => n.lL = 0)
  for (let i = 0; i < list.length * 3; i++) {
    let changed = false
    links.forEach(l => {
      const s = m[l.source], t = m[l.target]
      if (s && t && s.lL >= 0 && t.lL < s.lL + 1) { t.lL = s.lL + 1; changed = true }
    })
    if (!changed) break
  }
  list.forEach(n => { if (n.lL < 0) n.lL = 0 })
  const maxL = Math.max(...list.map(n => n.lL))
  // backward pass
  list.filter(n => !n.outs.length).forEach(n => n.lR = 0)
  for (let i = 0; i < list.length * 3; i++) {
    let changed = false
    links.forEach(l => {
      const s = m[l.source], t = m[l.target]
      if (s && t && t.lR >= 0 && s.lR < t.lR + 1) { s.lR = t.lR + 1; changed = true }
    })
    if (!changed) break
  }
  list.forEach(n => { if (n.lR < 0) n.lR = 0 })
  list.forEach(n => {
    if (align === 'left') n.layer = n.lL
    else if (align === 'right') n.layer = maxL - n.lR
    else n.layer = n.lL
  })
  return { m, list, maxL, numL: maxL + 1 }
}

const buildLayout = () => {
  const { cfg, W, H } = S
  const se = cfg.series
  const isV = se.orient === 'vertical'
  const { list, maxL, numL } = calcLayers(cfg.data, cfg.links, se.nodeAlign || 'justify')

  // assign colors
  list.forEach((n, i) => { if (!n.color && !n.itemStyle) n.color = PAL[i % PAL.length] })
  // collect by layer
  const lmap = {}
  list.forEach(n => { if (!lmap[n.layer]) lmap[n.layer] = []; lmap[n.layer].push(n) })

  // node weight = max flow
  list.forEach(n => {
    const inSum = cfg.links.filter(l => l.target === n.name).reduce((s, l) => s + (+l.value), 0)
    const outSum = cfg.links.filter(l => l.source === n.name).reduce((s, l) => s + (+l.value), 0)
    n.flow = Math.max(inSum, outSum, 0.1)
    n.labelOffsetX = n.labelOffsetX || 0; n.labelOffsetY = n.labelOffsetY || 0
  })

  // Margins for labels
  const lb = se.label || {}, fs = +(lb.fontSize || 12), lp = lb.position || 'right'
  const ml = lp === 'left' ? fs * 5.5 + 14 : 14
  const mr = lp === 'right' ? fs * 5.5 + 14 : 14
  const mt = lp === 'top' ? fs + 22 : 16
  const mb = lp === 'bottom' ? fs + 22 : 16
  const cW = W - ml - mr, cH = H - mt - mb
  const gap = +(se.nodeGap || 14), nw = +(se.nodeWidth || 18), nms = +(se.nodeMinSize || 6)

  // compute scale to fit
  let scale
  const maxFlowInLayer = Math.max(...Object.values(lmap).map(g => g.reduce((s, n) => s + n.flow, 0)), 1)
  const maxNodesInLayer = Math.max(...Object.values(lmap).map(g => g.length), 1)

  if (isV) {
    scale = (cW - (maxNodesInLayer - 1) * gap) / maxFlowInLayer * 0.9
    Object.entries(lmap).forEach(([li, ln]) => {
      const iy = +li
      const y = mt + (numL <= 1 ? cH / 2 : (iy / maxL) * cH)
      const totW = ln.reduce((s, n) => s + Math.max(n.flow * scale, nms), 0) + (ln.length - 1) * gap
      let x = ml + (cW - totW) / 2
      ln.forEach(n => { n.x = x; n.y = y; n.w = Math.max(n.flow * scale, nms); n.h = nw; x += n.w + gap })
    })
  } else {
    scale = (cH - (maxNodesInLayer - 1) * gap) / maxFlowInLayer * 0.9
    Object.entries(lmap).forEach(([li, ln]) => {
      const ix = +li
      const x = ml + (numL <= 1 ? cW / 2 : (ix / maxL) * cW)
      const totH = ln.reduce((s, n) => s + Math.max(n.flow * scale, nms), 0) + (ln.length - 1) * gap
      let y = mt + (cH - totH) / 2
      ln.forEach(n => { n.x = x; n.y = y; n.w = nw; n.h = Math.max(n.flow * scale, nms); y += n.h + gap })
    })
  }

  const nm = {}; list.forEach(n => nm[n.name] = n)
  S.nodes = list
  S.links = cfg.links.map(l => {
    const s = nm[l.source], t = nm[l.target]; if (!s || !t) return null
    return { ...l, lh: Math.max((+l.value) * scale, 1), sNode: s, tNode: t, labelOffsetX: 0, labelOffsetY: 0 }
  }).filter(Boolean)
  doReflow()
}

const doReflow = () => {
  const { nodes, links, cfg } = S
  const isV = cfg.series.orient === 'vertical'
  nodes.forEach(n => { n.sA = 0; n.tA = 0 })
  // sort by target/source position
  nodes.forEach(n => {
    if (!n._outs) n._outs = links.filter(l => l.sNode === n).sort((a, b) => isV ? (a.tNode.x - b.tNode.x) : (a.tNode.y - b.tNode.y))
    if (!n._ins) n._ins = links.filter(l => l.tNode === n).sort((a, b) => isV ? (a.sNode.x - b.sNode.x) : (a.sNode.y - b.sNode.y))
  })
  nodes.forEach(n => {
    n._outs.forEach(l => {
      if (isV) { l.sx = l.sNode.x + l.sNode.sA; l.sy = l.sNode.y + l.sNode.h }
      else { l.sx = l.sNode.x + l.sNode.w; l.sy = l.sNode.y + l.sNode.sA }
      l.sNode.sA += l.lh
    })
    n._ins.forEach(l => {
      if (isV) { l.tx = l.tNode.x + l.tNode.tA; l.ty = l.tNode.y }
      else { l.tx = l.tNode.x; l.ty = l.tNode.y + l.tNode.tA }
      l.tNode.tA += l.lh
    })
  })
}

// ── Render ──
const drawAll = (dn) => {
  const canvas = canvasRef.value
  if (!canvas || !S.nodes.length) return
  const ctx = canvas.getContext('2d')
  const dpr = window.devicePixelRatio || 1
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, S.W, S.H)

  // background
  ctx.fillStyle = props.backgroundColor
  ctx.fillRect(0, 0, S.W, S.H)

  const { nodes, links, cfg } = S
  const se = cfg.series, isV = se.orient === 'vertical'
  const ls = se.lineStyle || {}, lcv = +(ls.curveness ?? 0.6), lop = +(ls.opacity ?? 0.42), lco = ls.color || 'gradient'

  // draw links
  links.forEach(l => {
    const { sx, sy, tx, ty, lh, sNode: s, tNode: t } = l
    ctx.beginPath()
    if (isV) {
      const cp = (Math.abs(ty - sy) * 0.55 + 10) * lcv
      ctx.moveTo(sx, sy); ctx.bezierCurveTo(sx, sy + cp, tx, ty - cp, tx, ty)
      ctx.lineTo(tx + lh, ty); ctx.bezierCurveTo(tx + lh, ty - cp, sx + lh, sy + cp, sx + lh, sy)
    } else {
      const cp = (Math.abs(tx - sx) * 0.55 + 10) * lcv
      ctx.moveTo(sx, sy); ctx.bezierCurveTo(sx + cp, sy, tx - cp, ty, tx, ty)
      ctx.lineTo(tx, ty + lh); ctx.bezierCurveTo(tx - cp, ty + lh, sx + cp, sy + lh, sx, sy + lh)
    }
    ctx.closePath()
    if (lco === 'gradient') {
      const g = isV ? ctx.createLinearGradient(0, sy, 0, ty) : ctx.createLinearGradient(sx, 0, tx, 0)
      g.addColorStop(0, h2r(s.color || s.itemStyle?.color, lop))
      g.addColorStop(1, h2r(t.color || t.itemStyle?.color, lop))
      ctx.fillStyle = g
    } else if (lco === 'source') {
      ctx.fillStyle = h2r(s.color || s.itemStyle?.color, lop)
    } else if (lco === 'target') {
      ctx.fillStyle = h2r(t.color || t.itemStyle?.color, lop)
    } else {
      ctx.fillStyle = h2r(lco, lop)
    }
    ctx.fill()
  })

  // draw nodes
  nodes.forEach(n => {
    n.x = clamp(n.x, 0, S.W - n.w)
    n.y = clamp(n.y, 0, S.H - n.h)
    const isDrag = n === dn
    if (isDrag) {
      ctx.strokeStyle = h2r(n.color || n.itemStyle?.color, 0.95)
      ctx.lineWidth = 2; rrp(ctx, n.x - 2, n.y - 2, n.w + 4, n.h + 4, 4); ctx.stroke()
    }
    ctx.fillStyle = isDrag ? h2r(n.color || n.itemStyle?.color, 1) : (n.color || n.itemStyle?.color || PAL[0])
    rrp(ctx, n.x, n.y, n.w, n.h, 3); ctx.fill()
  })

  // draw labels
  const lb = se.label || {}
  if (lb.show !== false) {
    nodes.forEach(n => {
      const lp = lb.position || 'right', fs = +(lb.fontSize || 12)
      const text = n.name
      const nx = n.x, ny = n.y, nw = n.w, nh = n.h, cx = nx + nw / 2, cy = ny + nh / 2
      ctx.font = `${lb.fontWeight || 'normal'} ${fs}px sans-serif`
      const tw = ctx.measureText(text).width
      const pad = Array.isArray(lb.padding) ? lb.padding : [+(lb.padding || 2), +(lb.padding || 4)]
      const [pv, ph] = pad
      const bw = tw + ph * 2, bh = fs + pv * 2
      let cx2, cy2
      if (lp === 'right') { cx2 = nx + nw + 6 + bw / 2 + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
      else if (lp === 'left') { cx2 = nx - 6 - bw / 2 + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
      else if (lp === 'top') { cx2 = cx + (n.labelOffsetX || 0); cy2 = ny - 6 - bh / 2 + (n.labelOffsetY || 0) }
      else if (lp === 'bottom') { cx2 = cx + (n.labelOffsetX || 0); cy2 = ny + nh + 6 + bh / 2 + (n.labelOffsetY || 0) }
      else { cx2 = cx + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
      drawLabel(ctx, text, clamp(cx2, bw / 2, S.W - bw / 2), clamp(cy2, bh / 2, S.H - bh / 2), lb, { sourceColor: n.color || PAL[0], targetColor: n.color || PAL[0] })
    })
  }

  // Edge labels
  const el = se.edgeLabel || {}
  if (el.show !== false) {
    links.forEach(l => {
      const { sx, sy, tx, ty, lh, sNode: s, tNode: t } = l
      const fs = +(el.fontSize || 11)
      ctx.font = `${el.fontWeight || 'normal'} ${fs}px sans-serif`
      const text = fmtV(l.value)
      const tw = ctx.measureText(text).width
      const pad = Array.isArray(el.padding) ? el.padding : [+(el.padding || 1), +(el.padding || 3)]
      const [pv, ph] = pad
      const bw = tw + ph * 2, bh = fs + pv * 2
      let cx, cy
      if (isV) { cx = (sx + tx) / 2 + lh / 2; cy = (sy + ty) / 2 }
      else { cx = (sx + tx) / 2; cy = (sy + ty) / 2 + lh / 2 }
      cx += (l.labelOffsetX || 0); cy += (l.labelOffsetY || 0)
      drawLabel(ctx, text, clamp(cx, bw / 2, S.W - bw / 2), clamp(cy, bh / 2, S.H - bh / 2), el, { sourceColor: s.color || PAL[0], targetColor: t.color || PAL[0] })
    })
  }
}

const fmtV = (v) => v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1)

// ── Interaction ──
const evP = (e) => {
  const canvas = canvasRef.value; if (!canvas) return { x: 0, y: 0 }
  const r = canvas.getBoundingClientRect(); const src = e.touches ? e.touches[0] : e
  return { x: (src.clientX - r.left), y: (src.clientY - r.top) }
}

const hitN = (mx, my) => {
  for (let i = S.nodes.length - 1; i >= 0; i--) {
    const n = S.nodes[i]
    if (mx >= n.x && mx <= n.x + n.w && my >= n.y && my <= n.y + n.h) return n
  }
  return null
}

const hitLabel = (mx, my) => {
  const lb = S.cfg?.series?.label || {}
  if (lb.show === false) return null
  const fs = +(lb.fontSize || 12), lp = lb.position || 'right'
  const pad = Array.isArray(lb.padding) ? lb.padding : [+(lb.padding || 2), +(lb.padding || 4)]
  const [pv, ph] = pad
  // measure text width roughly
  for (let i = S.nodes.length - 1; i >= 0; i--) {
    const n = S.nodes[i]
    const text = n.name
    const tw = text.length * fs * 0.6 // approximate
    const bw = tw + ph * 2, bh = fs + pv * 2
    const nx = n.x, ny = n.y, nw = n.w, nh = n.h, cx = nx + nw / 2, cy = ny + nh / 2
    let cx2, cy2
    if (lp === 'right') { cx2 = nx + nw + 6 + bw / 2 + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
    else if (lp === 'left') { cx2 = nx - 6 - bw / 2 + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
    else if (lp === 'top') { cx2 = cx + (n.labelOffsetX || 0); cy2 = ny - 6 - bh / 2 + (n.labelOffsetY || 0) }
    else if (lp === 'bottom') { cx2 = cx + (n.labelOffsetX || 0); cy2 = ny + nh + 6 + bh / 2 + (n.labelOffsetY || 0) }
    else { cx2 = cx + (n.labelOffsetX || 0); cy2 = cy + (n.labelOffsetY || 0) }
    const bx = cx2 - bw / 2, by = cy2 - bh / 2
    if (mx >= bx && mx <= bx + bw && my >= by && my <= by + bh) return n
  }
  return null
}

const hitEdgeLabel = (mx, my) => {
  const el = S.cfg?.series?.edgeLabel || {}
  if (el.show === false) return null
  const fs = +(el.fontSize || 12), isV = S.cfg?.series?.orient === 'vertical'
  const pad = Array.isArray(el.padding) ? el.padding : [+(el.padding || 2), +(el.padding || 4)]
  const [pv, ph] = pad
  for (let i = S.links.length - 1; i >= 0; i--) {
    const l = S.links[i]
    const text = fmtV(l.value)
    const tw = text.length * fs * 0.6
    const bw = tw + ph * 2, bh = fs + pv * 2
    const { sx, sy, tx, ty, lh } = l
    let cx, cy
    if (isV) { cx = (sx + tx) / 2 + lh / 2; cy = (sy + ty) / 2 }
    else { cx = (sx + tx) / 2; cy = (sy + ty) / 2 + lh / 2 }
    cx += (l.labelOffsetX || 0); cy += (l.labelOffsetY || 0)
    const bx = cx - bw / 2, by = cy - bh / 2
    if (mx >= bx && mx <= bx + bw && my >= by && my <= by + bh) return l
  }
  return null
}

const onDown = (e) => {
  const { x, y } = evP(e)
  // Check edge labels first (smallest target)
  const edgeLink = hitEdgeLabel(x, y)
  if (edgeLink) {
    drag = { type: 'edgeLabel', l: edgeLink, ox: x - (edgeLink.labelOffsetX || 0), oy: y - (edgeLink.labelOffsetY || 0) }
    canvasRef.value.style.cursor = 'grabbing'; e.preventDefault(); return
  }
  // Check node labels next
  const labelNode = hitLabel(x, y)
  if (labelNode) {
    drag = { type: 'label', n: labelNode, ox: x - (labelNode.labelOffsetX || 0), oy: y - (labelNode.labelOffsetY || 0) }
    canvasRef.value.style.cursor = 'grabbing'; e.preventDefault(); return
  }
  // Check nodes last
  const node = hitN(x, y)
  if (node) {
    drag = { type: 'node', n: node, ox: x - node.x, oy: y - node.y }
    canvasRef.value.style.cursor = 'grabbing'; e.preventDefault()
  }
}

const onMove = (e) => {
  if (!drag) return; const { x, y } = evP(e)
  if (drag.type === 'node') {
    drag.n.x = clamp(x - drag.ox, 0, S.W - drag.n.w)
    drag.n.y = clamp(y - drag.oy, 0, S.H - drag.n.h)
    doReflow(); drawAll(drag.n)
  } else if (drag.type === 'label') {
    drag.n.labelOffsetX = x - drag.ox
    drag.n.labelOffsetY = y - drag.oy
    drawAll(null)
  } else if (drag.type === 'edgeLabel') {
    drag.l.labelOffsetX = x - drag.ox
    drag.l.labelOffsetY = y - drag.oy
    drawAll(null)
  }
  e.preventDefault()
}

const onUp = () => {
  if (!drag) return; drag = null; canvasRef.value.style.cursor = 'default'
  doReflow(); drawAll(null)
}

// ── Init ──
const initCanvas = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  // Use prop dimensions, zoomLevel adjusts visual scale
  const z = props.zoomLevel
  S.W = Math.round(props.imageWidth * z)
  S.H = Math.round(props.imageHeight * z)
  canvas.width = Math.round(S.W * dpr)
  canvas.height = Math.round(S.H * dpr)
  canvas.style.width = S.W + 'px'
  canvas.style.height = S.H + 'px'
  canvas.style.backgroundColor = props.backgroundColor

  // Build config
  const links = props.chartData.map(d => ({ source: d.source, target: d.target, value: +d.value }))
  const nameSet = new Set(); links.forEach(l => { nameSet.add(l.source); nameSet.add(l.target) })
  const sortedNames = [...nameSet].sort()
  const data = sortedNames.map((name, i) => ({
    name, color: PAL[i % PAL.length], borderColor: PAL[i % PAL.length]
  }))

  S.cfg = {
    data, links,
    series: {
      orient: props.orient, nodeAlign: props.nodeAlign,
      nodeWidth: props.nodeWidth, nodeGap: props.nodeGap, nodeMinSize: 6,
      lineStyle: {
        color: props.lineStyleColor === 'gradient' ? 'gradient' : props.lineStyleColor === 'source' ? 'source' : 'gradient',
        opacity: props.lineOpacity, curveness: props.selectedLineColorCurveness
      },
      label: { show: true, position: props.labelPosition, fontSize: props.labelFontSize, fontWeight: 'bold', color: '#333', backgroundColor: 'transparent', borderColor: 'transparent', borderWidth: 0, borderRadius: 3, padding: [2, 4] },
      edgeLabel: { show: true, fontSize: 11, fontWeight: 'normal', color: '#666', backgroundColor: 'rgba(255,255,255,0.85)', borderColor: 'transparent', borderWidth: 0, borderRadius: 2, padding: [1, 3] }
    }
  }

  buildLayout()
  drawAll(null)
}

// ── Export ──
const exportLayout = () => {
  const canvas = canvasRef.value
  if (!canvas) { ElMessage.error('图表未初始化'); return }
  try {
    const link = document.createElement('a')
    link.download = `sankey-chart-${Date.now()}.png`
    link.href = canvas.toDataURL('image/png')
    document.body.appendChild(link); link.click(); document.body.removeChild(link)
    ElMessage.success('导出成功')
  } catch (e) { ElMessage.error('导出失败') }
}

const exportSVG = () => {
  if (!S.nodes.length) { ElMessage.error('图表未初始化'); return }
  const { nodes, links, cfg, W, H } = S
  const se = cfg.series, isV = se.orient === 'vertical'
  const ls = se.lineStyle || {}, lcv = +(ls.curveness ?? 0.6), lop = +(ls.opacity ?? 0.42), lco = ls.color || 'gradient'
  const lb = se.label || {}

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
  svg += `<rect width="100%" height="100%" fill="${props.backgroundColor}"/>`

  // Gradient defs
  let defs = '', gid = 0
  const gradIds = {}

  links.forEach(l => {
    const { sx, sy, tx, ty, lh, sNode: s, tNode: t } = l
    let d = ''
    if (isV) {
      const cp = (Math.abs(ty - sy) * 0.55 + 10) * lcv
      d = `M${sx},${sy} C${sx},${sy + cp} ${tx},${ty - cp} ${tx},${ty} L${tx + lh},${ty} C${tx + lh},${ty - cp} ${sx + lh},${sy + cp} ${sx + lh},${sy} Z`
    } else {
      const cp = (Math.abs(tx - sx) * 0.55 + 10) * lcv
      d = `M${sx},${sy} C${sx + cp},${sy} ${tx - cp},${ty} ${tx},${ty} L${tx},${ty + lh} C${tx - cp},${ty + lh} ${sx + cp},${sy + lh} ${sx},${sy + lh} Z`
    }
    let fill
    if (lco === 'gradient') {
      const key = `${s.name}-${t.name}`
      if (!gradIds[key]) {
        gid++
        gradIds[key] = 'g' + gid
        if (isV) {
          defs += `<linearGradient id="g${gid}" x1="0" y1="${sy}" x2="0" y2="${ty}" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="${s.color || PAL[0]}" stop-opacity="${lop}"/><stop offset="1" stop-color="${t.color || PAL[0]}" stop-opacity="${lop}"/></linearGradient>`
        } else {
          defs += `<linearGradient id="g${gid}" x1="${sx}" y1="0" x2="${tx}" y2="0" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="${s.color || PAL[0]}" stop-opacity="${lop}"/><stop offset="1" stop-color="${t.color || PAL[0]}" stop-opacity="${lop}"/></linearGradient>`
        }
      }
      fill = `url(#${gradIds[key]})`
    } else if (lco === 'source') {
      fill = h2r(s.color || PAL[0], lop)
    } else if (lco === 'target') {
      fill = h2r(t.color || PAL[0], lop)
    } else {
      fill = h2r(lco, lop)
    }
    svg += `<path d="${d}" fill="${fill}"/>`
  })

  if (defs) svg += `<defs>${defs}</defs>`

  // Nodes
  nodes.forEach(n => {
    const c = n.color || n.itemStyle?.color || PAL[0]
    svg += `<rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="3" fill="${c}"/>`
  })

  // Labels
  if (lb.show !== false) {
    nodes.forEach(n => {
      const lp = lb.position || 'right', fs = +(lb.fontSize || 12)
      const text = n.name
      const nx = n.x, ny = n.y, nw = n.w, nh = n.h, cx = nx + nw / 2, cy = ny + nh / 2
      let lx, ly, anchor = 'start'
      if (lp === 'right') { lx = nx + nw + 6 + (n.labelOffsetX || 0); ly = cy + (n.labelOffsetY || 0); anchor = 'start' }
      else if (lp === 'left') { lx = nx - 6 + (n.labelOffsetX || 0); ly = cy + (n.labelOffsetY || 0); anchor = 'end' }
      else if (lp === 'top') { lx = cx + (n.labelOffsetX || 0); ly = ny - 6 + (n.labelOffsetY || 0); anchor = 'middle' }
      else if (lp === 'bottom') { lx = cx + (n.labelOffsetX || 0); ly = ny + nh + 6 + fs + (n.labelOffsetY || 0); anchor = 'middle' }
      else { lx = cx + (n.labelOffsetX || 0); ly = cy + (n.labelOffsetY || 0); anchor = 'middle' }
      svg += `<text x="${lx}" y="${ly}" font-family="sans-serif" font-weight="${lb.fontWeight || 'bold'}" font-size="${fs}" fill="${lb.color || '#333'}" text-anchor="${anchor}" dominant-baseline="central">${text}</text>`
    })
  }

  svg += '</svg>'

  // Download
  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.download = `sankey-chart-${Date.now()}.svg`
  link.href = url
  document.body.appendChild(link); link.click(); document.body.removeChild(link)
  URL.revokeObjectURL(url)
  ElMessage.success('SVG 导出成功')
}

// ── Lifecycle ──
onMounted(() => {
  nextTick(() => {
    initCanvas()
    const canvas = canvasRef.value
    if (canvas) {
      canvas.addEventListener('mousedown', onDown); canvas.addEventListener('mousemove', onMove)
      canvas.addEventListener('mouseup', onUp); canvas.addEventListener('mouseleave', onUp)
    }
  })
})

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId)
  if (resizeObserver) resizeObserver.disconnect()
  const canvas = canvasRef.value
  if (canvas) {
    canvas.removeEventListener('mousedown', onDown); canvas.removeEventListener('mousemove', onMove)
    canvas.removeEventListener('mouseup', onUp); canvas.removeEventListener('mouseleave', onUp)
  }
})

watch([() => props.chartData, () => props.orient, () => props.nodeAlign, () => props.labelPosition, () => props.lineStyleColor, () => props.selectedLineColorCurveness, () => props.backgroundColor, () => props.imageWidth, () => props.imageHeight, () => props.zoomLevel], () => { nextTick(() => initCanvas()) }, { deep: true })

defineExpose({ updateChart: initCanvas, exportLayout, exportSVG })
</script>

<style scoped>
canvas { cursor: default; display: block; }
</style>
